<template>
  <div class="payment-create main-content">
    <form action="" @submit.prevent="submitPaymentData">
      <div class="page-header">
        <h3 class="page-title">
          {{ $t("prepayments.invoice_payment_request") }}
        </h3>
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/anchor/dashboard">{{
              $t("general.home")
            }}</router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link slot="item-title" to="/anchor/invoices">{{
              $tc("invoices.invoice", 2)
            }}</router-link>
          </li>

          <li class="breadcrumb-item">
            {{ $t("prepayments.invoice_payment_request") }}
          </li>
        </ol>

        <div class="page-actions row">
          <div class="col-xs-2 mr-4">
            <base-button
              v-if="isRejectedButtonShow"
              :outline="true"
              :loading="isLoading"
              size="large"
              icon="times"
              color="theme"
              @click="rejectedPrepaymentRequest()"
            >
              {{ $t("general.reject") }}
            </base-button>
          </div>
          <div class="col-xs-2">
            <base-button
              v-if="isApprovedButtonShow"
              :loading="isLoading"
              size="large"
              icon="save"
              color="theme"
              type="submit"
            >
              {{ $t("general.approve") }}
            </base-button>
          </div>
        </div>
      </div>

      <SingleInvoiceRow :fetch-data="fetchData" />

      <div class="payment-card card">
        <div class="card-body">
          <div class="row">
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t("prepayments.date") }}</label
                ><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.payment_date"
                  :calendar-button="true"
                  :disabled="true"
                  calendar-button-icon="calendar"
                  @change="$v.formData.payment_date.$touch()"
                />
                <div v-if="$v.formData.payment_date.$error">
                  <span
                    v-if="!$v.formData.payment_date.required"
                    class="text-danger"
                  >{{ $t("validation.required") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("general.promised_payment_date")
                }}</label
                ><span class="text-danger"> *</span>
                <base-date-picker
                  v-model="formData.promised_payment_date"
                  :calendar-button="true"
                  :disabled-dates="{
                    to: new Date(Date.now())
                  }"
                  calendar-button-icon="calendar"
                  @change="$v.formData.promised_payment_date.$touch()"
                />
                <div v-if="$v.formData.promised_payment_date.$error">
                  <span
                    v-if="!$v.formData.promised_payment_date.required"
                    class="text-danger"
                  >{{ $t("validation.required") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.prepayment_request_number")
                }}</label
                ><span class="text-danger"> *</span>
                <div class="base-read-box base-read-box-field">
                  <span v-html="requestNumber"/>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.payment_mode")
                }}</label>
                <div class="base-read-box base-read-box-field">
                  <span v-if="formData.payment_method" v-html="formData.payment_method.name"/>
                  <span v-else v-html="'No Payment Mode Select'"/>
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{ $t("prepayments.amount") }}</label
                ><span class="text-danger"> *</span>
                <div class="base-read-box base-read-box-field">
                  <div v-html="$utils.formatMoney(formData.requested_amount, customerCurrency)" />
                </div>
              </div>
            </div>
            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.offer_requested_on_date")
                }}</label>
                <div class="base-read-box base-read-box-field">
                  <span
                    v-if="
                      formData.supplier_offer_request &&
                        formData.supplier_offer_request == 'as_soon_as_possible'
                    "
                    v-html="'As Soon as Possible'"
                  />
                  <span
                    v-else-if="
                      formData.supplier_offer_request &&
                        formData.supplier_offer_request == 'select_a_date'
                    "
                    v-html="formData.formattedSupplierOfferRequestDate"
                  />
                </div>
              </div>
            </div>

            <div class="col-sm-6">
              <div class="form-group">
                <label class="form-label">{{
                  $t("general.cashflow_value")
                }}</label
                ><span class="text-danger"> *</span>
                <div class="base-input">
                  <money
                    v-model="cashflowValue"
                    v-bind="customerCurrency"
                    class="input-field"
                  />
                </div>
              </div>
            </div>

            <!--
            <div class="col-sm-6">
              <div class="form-group">
                <label>{{ $t('general.indicative_withholding_tax') }}</label>
                <div class="base-read-box base-read-box-field">
                   <span v-if="formData.suggest_withholding_tax_rate" v-html="formData.suggest_withholding_tax_rate"></span>
                   <span v-else v-html="'No Indicative Withholding Tax'"></span>
                </div>
              </div>
            </div>
            -->
            <div class="col-sm-6">
              <div class="form-group">
                <label>{{ $t("general.withholding_tax_rate") }}</label>
                <div class="base-input">
                  <money
                    :class="{
                      invalid: $v.formData.withholding_tax_rate.$error
                    }"
                    v-model="formData.withholding_tax_rate"
                    v-bind="percentageFormatMoneyComponent"
                    class="input-field"
                  />
                </div>
                <div v-if="$v.formData.withholding_tax_rate.$error">
                  <span
                    v-if="!$v.formData.withholding_tax_rate.between"
                    class="form-group__message text-danger"
                  >{{ $t("validation.enter_valid_tax_rate") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.supplier_comment")
                }}</label>
                <div class="base-read-box base-read-box-field">
                  <span v-if="formData.supplier_comment" v-html="formData.supplier_comment"/>
                  <span v-else v-html="'No Supplier Comment'"/>
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.administrator_comment")
                }}</label>
                <div class="base-read-box base-read-box-field">
                  <span v-if="formData.superAdmin_comment" v-html="formData.superAdmin_comment"/>
                  <span v-else v-html="'No Administrator Comment'"/>
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group">
                <label class="form-label">{{
                  $t("prepayments.anchor_comment")
                }}</label>
                <base-text-area
                  v-model="formData.anchor_comment"
                  @input="$v.formData.anchor_comment.$touch()"
                />
                <div v-if="$v.formData.anchor_comment.$error">
                  <span
                    v-if="!$v.formData.anchor_comment.maxLength"
                    class="text-danger"
                  >{{ $t("validation.notes_maxlength") }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-sm-12">
              <div class="form-group collapse-button-container">
                <base-button
                  :loading="isLoading"
                  icon="save"
                  color="theme"
                  type="submit"
                  class="collapse-button"
                >
                  {{ $t("prepayments.save_prepayment") }}
                </base-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import SingleInvoiceRow from '../invoices/SingleInvoiceRow'
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import moment from 'moment'
const { required, between, maxLength } = require('vuelidate/lib/validators')

export default {
  components: { MultiSelect, SingleInvoiceRow },
  mixins: [validationMixin],
  data () {
    return {
      anchorCommentDiv: false,
      superAdminCommentDiv: false,
      customerCurrency: null,

      userType: null,
      invoice: {
        loan_information: {}
      },
      formData: {
        suggest_withholding_tax_rate: 0,
        withholding_tax_rate: 0,
        // sales_tax_withholding: 0,

        payment_number: null,
        payment_date: null,
        promised_payment_date: null,
        requested_amount: 0,
        payment_method: null,
        invoice_id: null,
        supplier_comment: null,
        superAdmin_comment: null,
        anchor_comment: null,
        user_id: null,
        user_company_id: null,
        customer_id: null,
        cashflowValue: 0,
        supplier_offer_request: null,
        formattedSupplierOfferRequestDate: null
      },
      isLoading: false,
      isRejectedButtonShow: true,
      isApprovedButtonShow: true,

      fetchData: [],
      percentageFormat: {
        precision: 0,
        thousand_separator: ',',
        decimal_separator: '.',
        symbol: '% '
      },
      percentageFormatMoneyComponent: {
        decimal: '.',
        thousands: ',',
        prefix: '% ',
        precision: 2,
        masked: false
      },
      requestNumber: null,
      GST: 0,
      invoiceAmount: 0
    }
  },
  computed: {
    ...mapGetters('currency', ['defaultCurrencyForInput']),
    ...mapGetters('payment', ['paymentModes']),
    cashflowValue: {
      get: function () {
        const requestedAmount = Number(this.formData.requested_amount || 0)
        return (Math.floor(requestedAmount * 100) / 100).toFixed(2)
      },
      set: function (val) {
        this.formData.requested_amount = val
      }
    }
  },
  created () {
    if (localStorage.getItem('role') === 'super_admin') {
      this.userType = 'super_admin'
    } else if (localStorage.getItem('account_type') === 'anchor') {
      this.userType = 'anchor'
    } else if (localStorage.getItem('account_type') === 'supplier') {
      this.userType = 'supplier'
    } else if (localStorage.getItem('account_type') === 'funder') {
      this.userType = 'funder'
    }
    this.loadInvoice()
  },
  validations () {
    return {
      formData: {
        payment_date: {
          required
        },
        promised_payment_date: {
          required
        },
        anchor_comment: {
          maxLength: maxLength(255)
        },
        withholding_tax_rate: {
          between: between(0, 99)
        }
        // sales_tax_withholding:{
        //   between: between(0, 99)
        // }
      }
    }
  },
  async mounted () {
    this.$nextTick(() => {
      this.loadData()
    })
  },
  methods: {
    ...mapActions('invoice', [
      'fetchInvoice',
      'SingleInvoiceArray',
      'markAsAuthorized',
      'rejectedInvoice'
    ]),
    ...mapActions('prepayment', [
      'fetchCreatePayment',
      'addPayment',
      'updatePayment',
      'fetchEditPaymentData',
      'createPrepaymentRequest',
      'addPrepaymentRequest',
      'editPrepaymentRequest',
      'updatePrepaymentRequest'
    ]),
    ...mapActions('modal', ['openModal']),
    ...mapActions('LoanRequest', ['loanRequestStatus']),
    ...mapActions('companyImpactOthers', [
      'addCompanyImpactOthers',
      'getImpact'
    ]),
    async loadInvoice () {
      let response = await this.SingleInvoiceArray(this.$route.params.id)
      if (response.data) {
        this.fetchData = response.data.invoice
      }
    },
    filter_taxes (tax) {
      return tax.name == 'GST'
    },
    async loadData () {
      this.isLoading = true
      let response = await this.editPrepaymentRequest(this.$route.params.id)
      this.isLoading = false

      this.formData.supplier_comment =
        response.data.prepaymentRequest.supplier_comment
      this.formData.superAdmin_comment =
        response.data.prepaymentRequest.superAdmin_comment

      this.formData.supplier_offer_request =
        response.data.prepaymentRequest.supplier_offer_request
      this.formData.formattedSupplierOfferRequestDate =
        response.data.prepaymentRequest.formattedSupplierOfferRequestDate

      if (
        response.data.prepaymentRequest.invoice &&
        response.data.prepaymentRequest.invoice.suggest_withholding_tax_rate
      ) {
        this.formData.suggest_withholding_tax_rate =
          '%' +
          response.data.prepaymentRequest.invoice.suggest_withholding_tax_rate
      } else {
        this.formData.suggest_withholding_tax_rate = '%'
      }

      this.customerCurrency =
        response.data.prepaymentRequest.invoice.customer.currency

      this.formData.payment_date = moment(
        response.data.prepaymentRequest.prepayment_date,
        'YYYY-MM-DD'
      ).toString()

      this.formData.requested_amount =
        response.data.prepaymentRequest.requested_amount

      this.requestNumber =
        response.data.request_prefix + '-' + response.data.nextRequestNumber
      this.formData.payment_method =
        response.data.prepaymentRequest.payment_method

      this.formData.invoice_id = response.data.prepaymentRequest.invoice.id
      this.formData.invoice_expire_before =
        response.data.prepaymentRequest.invoice.invoiceExpiredBefore
      console.log(this.formData.invoice_expire_before != true)
      this.invoiceAmount = response.data.prepaymentRequest.invoice.total
      let GST_TAX_ARRAY = response.data.prepaymentRequest.invoice.taxes.filter(
        this.filter_taxes
      )
      if (GST_TAX_ARRAY && GST_TAX_ARRAY.length > 0) {
        this.GST = GST_TAX_ARRAY[0].amount
      }
      this.formData.user_id = response.data.prepaymentRequest.invoice.user_id
      this.formData.user_company_id =
        response.data.prepaymentRequest.invoice.company_id
      this.formData.customer_id =
        response.data.prepaymentRequest.invoice.customer_id
      this.formData.promised_payment_date = moment(response.data.prepaymentRequest.invoice.due_date)
        .toString()
      this.getCompanyImpactOnAccount()
      return true
    },
    async getCompanyImpactOnAccount () {
      this.isLoading = true
      let data = {
        company_id: localStorage.getItem('selectedCompany'),
        guest_company_id: this.formData.user_company_id
      }
      let response = await this.getImpact(data)
      this.isLoading = false
      if (response.data && response.data.withholding_tax_rate) {
        this.formData.withholding_tax_rate = response.data.withholding_tax_rate
      }
    },

    async submitPaymentData () {
      this.$v.formData.$touch()
      if (this.$v.$invalid) {
        return true
      }
      let data = {
        id: this.$route.params.id,
        anchor_comment: this.formData.anchor_comment,
        withholding_tax_rate: this.formData.withholding_tax_rate,
        requested_amount: this.formData.requested_amount,
        // sales_tax_withholding: this.formData.sales_tax_withholding,
        promised_payment_date: moment(
          this.formData.promised_payment_date
        ).format('DD/MM/YYYY')
      }
      this.isLoading = true
      this.isRejectedButtonShow = false
      let response = await this.updatePrepaymentRequest(data)
      if (response.data.success) {
        if (this.userType === 'anchor') {
          const data = {
            invoice_id: this.$route.params.id,
            request_level: 'Approved'
          }
          let response = await this.loanRequestStatus(data)
          if (response.data) {
            window.toastr['success'](this.$t('prepayments.prepayment_updated_message'))
            if (this.formData.invoice_expire_before != true) {
              let res = await this.markAsAuthorized({invoice_id: this.formData.invoice_id})
              if (res.data) {
                this.isLoading = false
                this.invoiceStatus = res.data.invoice.status
                window.toastr['success'](this.$tc('invoices.mark_as_authorized_successfully'))
              }
            }
          }
          this.$router.push('/anchor/invoices')
        }
        return true
      }
    },
    async rejectedPrepaymentRequest () {
      const data = {
        invoice_id: this.formData.invoice_id,
        request_level: 'Rejected'
      }
      this.isApprovedButtonShow = false
      this.isLoading = true
      let response = await this.loanRequestStatus(data)
      if (response.data.success) {
        if (this.userType === 'anchor') {
          this.$router.push('/anchor/invoices')
        }
        this.isLoading = false
        window.toastr['success'](
          this.$tc('prepayments.prepayment_rejected_message')
        )
        return true
      }
    }
  }
}
</script>
<style scoped>
.table-component {
  min-height: 0px;
}
</style>
